import React from 'react';
import {
  Box,
  Paper,
  TablePagination,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AccountingDataTable from '../../shared/AccountingDataTable';
import type { DataTableColumn } from '../../shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';

interface InventoryStyleListTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  loading?: boolean;

  // Server-side sayfalama
  totalCount: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;

  // Server-side sıralama
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';

  onRowClick?: (row: T) => void;
  clickable?: boolean;

  emptyMessage?: string;
  ariaLabel?: string;
  columnResizeStorageKey?: string;
  rowsPerPageOptions?: number[];

  /** Üst çubuğun solunda gösterilecek bilgi (ör. kayıt sayısı). */
  toolbarInfo?: React.ReactNode;
}

/**
 * Stok yönetimi tablosunun (`InventoryTable`) dış kabuğunu yeniden kullanan
 * liste tablosu: `Paper elevation={3}` + üstte ince sayfalama şeridi +
 * `AccountingDataTable` + altta sayfalama şeridi.
 *
 * Stok listesiyle aynı görünümü isteyen yardımcı sayfalar (basit üretim
 * reçeteleri, menüler) bunu kullanır; kabuğu her sayfada tekrar yazmak
 * görünümün zamanla ayrışmasına yol açıyordu.
 */
export function InventoryStyleListTable<T>({
  columns,
  rows,
  getRowId,
  loading,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  sortField,
  sortDirection,
  onRowClick,
  clickable,
  emptyMessage,
  ariaLabel,
  columnResizeStorageKey,
  rowsPerPageOptions = [25, 50, 100, 200, 300, 400, 500],
  toolbarInfo,
}: InventoryStyleListTableProps<T>) {
  const { t } = useTranslation('accounting');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Üst sayfalama şeridi — stok listesinde olduğu gibi yalnızca masaüstünde */}
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            minHeight: '40px',
            height: '40px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
            {typeof toolbarInfo === 'string' ? (
              <Typography variant="caption" color="text.secondary">
                {toolbarInfo}
              </Typography>
            ) : (
              toolbarInfo
            )}
          </Box>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={rowsPerPageOptions}
            labelRowsPerPage={t('stock.pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('stock.pagination.displayedRows', { from, to, count })}
            sx={{
              '& .MuiTablePagination-toolbar': {
                minHeight: '40px',
                height: '40px',
                padding: '0 8px',
              },
              '& .MuiTablePagination-selectLabel': { margin: 0 },
              '& .MuiTablePagination-displayedRows': { margin: 0 },
            }}
          />
        </Box>
      )}

      <AccountingDataTable<T>
        columns={columns}
        rows={rows}
        getRowId={getRowId}
        loading={loading}
        pagination={false}
        elevation={0}
        tableLayout="auto"
        onSort={onSort}
        sortField={sortField}
        sortDirection={sortDirection}
        onRowClick={onRowClick}
        clickable={clickable}
        emptyMessage={emptyMessage}
        ariaLabel={ariaLabel}
        columnResizeStorageKey={columnResizeStorageKey}
      />

      {/* Alt sayfalama şeridi */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', md: 'flex-end' },
          borderTop: '1px solid rgba(224, 224, 224, 1)',
          minHeight: '40px',
        }}
      >
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
          labelRowsPerPage={isMobile ? '' : t('stock.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('stock.pagination.displayedRows', { from, to, count })}
          sx={{
            width: { xs: '100%', md: 'auto' },
            '& .MuiTablePagination-toolbar': {
              minHeight: '40px',
              height: '40px',
              padding: { xs: '0 4px', md: '0 8px' },
            },
            '& .MuiTablePagination-selectLabel': {
              margin: 0,
              display: { xs: 'none', md: 'block' },
            },
            '& .MuiTablePagination-displayedRows': { margin: 0 },
          }}
        />
      </Box>
    </Paper>
  );
}

export default InventoryStyleListTable;
